/**
 * useAlertStats Hook - Production Grade
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { alertsApi } from '../services/alertsApi';
import { AlertStats } from '../types';

// Global cache for stats - keyed by siteCode
let statsCache: Record<string, { data: AlertStats; timestamp: number }> = {};
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

export const useAlertStats = (autoLoad = true, siteCode?: string, selectedDate?: string) => {
  const [stats, setStats] = useState<AlertStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const isMountedRef = useRef<boolean>(true);

  const loadStats = useCallback(async (forceRefresh = false) => {
    const siteKey = siteCode || 'all';
    const dateKey = selectedDate || 'all-dates';
    const cacheKey = `${siteKey}::${dateKey}`;
    const cached = statsCache[cacheKey];

    // Check cache first
    if (!forceRefresh && cached) {
      const isExpired = Date.now() - cached.timestamp > CACHE_DURATION;
      if (!isExpired) {
setStats(cached.data);
        setLoading(false);
        return;
      }
    }

    setLoading(true);

    try {
const response = await alertsApi.fetchStats(siteCode, selectedDate);

      if (!isMountedRef.current) return;

      // Update cache
      statsCache[cacheKey] = {
        data: response,
        timestamp: Date.now()
      };

      setStats(response);
} catch (err: any) {
      if (!isMountedRef.current) return;

      const errorMsg = err?.message || 'Failed to load stats';
      console.error('Error loading stats:', errorMsg);
      setError(errorMsg);

    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [siteCode, selectedDate]);

  const refetch = useCallback(() => {
    return loadStats(true);
  }, [loadStats]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (autoLoad) {
      loadStats(false);
    }
  }, [autoLoad, loadStats, siteCode, selectedDate]);

  return { stats, loading, error, refetch };
};

